import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { INTERNAL_EMAIL_ON_JOB_ROLES } from '@/constants/on-job-roles';
import { useGetOrgRolesQuery } from '@/store/query/roles';
import { useGetHiringTeamOfJob } from '@/api/useGetHiringTeamOfJob/useGetHiringTeamOfJob';
import { orgRoleToken } from '@/utils/internalEmailRecipients';
import {
  fetchUserSearchPage,
  searchedUserName,
  USER_SEARCH_PAGE_SIZE,
} from '@/api/userSearch/fetchUserSearch';

export interface RecipientOption {
  /** The token written to the form — a user id, a job role, or `org_role:<id>`. */
  value: string;
  label: string;
  /** Users only: shown under the name. */
  email?: string;
  image?: string;
  /** Users only: their role on this job, e.g. "Hiring manager on job". */
  tag?: string;
  /** Roles only: the initials chip drawn in place of an avatar. */
  initials?: string;
  /** Roles nobody holds: selectable would mean addressing an empty set. */
  disabled?: boolean;
  /** Why the row is disabled, shown where a user row shows its tag. */
  hint?: string;
}

export interface RecipientSection {
  id: 'job_roles' | 'kula_roles' | 'users';
  title: string;
  /** Whole-category total — not the number of rows rendered. */
  count: number;
  options: RecipientOption[];
}

const initialsOf = (label: string) =>
  label
    .replace(/\(on [^)]*\)/i, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();

/** "job_hiring_manager" -> "Hiring manager on job", for the per-user tag. */
/**
 * Roles whose stored name does not read as its own label. `job_sourcer` is the
 * only one today: the backend calls it a sourcer and the whole product calls it
 * an external recruiter, so deriving the tag from the name would read
 * "Sourcer on job" to someone who has never seen the column name.
 */
const ROLE_TAG_OVERRIDES: Record<string, string> = {
  job_sourcer: 'External recruiter on job',
};

const EMPTY_JOB_ROLE_HINT = 'No one on this job';
const EMPTY_ORG_ROLE_HINT = 'No one in this role';

const onJobTagLabel = (roleName: string) => {
  const override = ROLE_TAG_OVERRIDES[roleName];
  if (override) return override;

  const words = roleName.replace(/^job_/, '').replace(/_/g, ' ').trim();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)} on job`;
};

export const recipientUserSearchKey = (query: string) =>
  ['internal-email-recipient-users', query] as const;

/**
 * The three sections behind the recipient picker.
 *
 * Job and Kula roles are small, fixed lists, so they arrive whole and filter in
 * memory. Users cannot: an account has thousands, so the section opens on the
 * first page and defers to the same server-side search the Cc/Bcc fields use
 * (ENG-3766). The badge reads `users_counts_by_status.active`, which the search
 * response carries — paging could never report the account total.
 */
export const useRecipientSections = (jobId?: string) => {
  const { data: orgRolesData } = useGetOrgRolesQuery({});
  const { data: hiringTeam } = useGetHiringTeamOfJob(String(jobId ?? ''), {
    enabled: Boolean(jobId),
  });

  // userId -> the role they hold on this job. First role wins: the tag is a
  // single chip, and a primary recruiter is also a recruiter.
  const tagByUserId = useMemo(() => {
    const map = new Map<string, string>();
    (hiringTeam?.roles ?? []).forEach((role) => {
      const label = onJobTagLabel(role.name);
      (role.users ?? []).forEach((user) => {
        const key = String((user as { id?: unknown })?.id ?? '');
        if (key && !map.has(key)) map.set(key, label);
      });
    });
    return map;
  }, [hiringTeam]);

  const memberCountByRole = useMemo(() => {
    const counts = new Map<string, number>();
    (hiringTeam?.roles ?? []).forEach((role) => {
      counts.set(role.name, (role.users ?? []).length);
    });
    return counts;
  }, [hiringTeam]);

  /**
   * The list is a fixed constant, so every job role is offered on every job —
   * but a role nobody holds resolves to nobody at send time, which surfaced as
   * `err_internal_email_no_recipients` only after the composer was submitted.
   *
   * Disabled, not hidden: the role still exists, and saying so is more useful
   * than a menu that changes shape per job. Only a loaded hiring team can prove
   * a role is empty — while it is missing, every role stays selectable rather
   * than the whole section greying out.
   */
  const jobRoleOptions = useMemo<RecipientOption[]>(
    () =>
      INTERNAL_EMAIL_ON_JOB_ROLES.map((role) => {
        const isEmpty =
          Boolean(hiringTeam) && (memberCountByRole.get(role.value) ?? 0) === 0;

        return {
          value: role.value,
          label: role.label,
          initials: initialsOf(role.label),
          disabled: isEmpty,
          ...(isEmpty ? { hint: EMPTY_JOB_ROLE_HINT } : {}),
        };
      }),
    [hiringTeam, memberCountByRole]
  );

  const orgRoleOptions = useMemo<RecipientOption[]>(() => {
    const roles =
      (
        orgRolesData as {
          org_roles?: Array<{
            id: number;
            name: string;
            members_count?: number;
          }>;
        }
      )?.org_roles ?? [];
    return roles.map((role) => {
      // `members_count` comes off the org_roles payload. Absent (an older
      // response) is unknown, not empty — only an explicit zero disables.
      const isEmpty = role.members_count === 0;

      return {
        value: orgRoleToken(role.id),
        label: role.name,
        initials: initialsOf(role.name),
        disabled: isEmpty,
        ...(isEmpty ? { hint: EMPTY_ORG_ROLE_HINT } : {}),
      };
    });
  }, [orgRolesData]);

  return { jobRoleOptions, orgRoleOptions, tagByUserId };
};

/**
 * Matches the debounce kula's own async Select applies to `loadOptions`, which
 * is what the Cc/Bcc user search runs on.
 */
const USER_SEARCH_DEBOUNCE_MS = 300;

/** Users for the picker: first page on open, server-side search on type. */
export const useRecipientUsers = (
  query: string,
  tagByUserId: Map<string, string>
) => {
  const typed = query.trim();

  // The two role sections filter in memory and stay instant; this one costs a
  // round trip, so it waits for a pause. Without it every keystroke fires its
  // own request and the list trails the input by a character or more.
  const [term, setTerm] = useState(typed);
  useEffect(() => {
    if (typed === term) return undefined;
    const id = setTimeout(() => setTerm(typed), USER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [typed, term]);
  const { data, isFetching } = useQuery({
    queryKey: recipientUserSearchKey(term),
    queryFn: ({ signal }) =>
      fetchUserSearchPage({
        query: term,
        items: USER_SEARCH_PAGE_SIZE,
        signal,
      }),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });

  const options = useMemo<RecipientOption[]>(
    () =>
      (data?.users ?? []).map((user) => ({
        value: String(user.id),
        label: searchedUserName(user),
        email: user.email ?? undefined,
        image: user.avatar_thumb_url ?? undefined,
        tag: tagByUserId.get(String(user.id)),
      })),
    [data, tagByUserId]
  );

  return {
    options,
    // Whole-account total while unfiltered; the match count once searching.
    count: term ? options.length : (data?.counts?.active ?? options.length),
    // Also true while the debounce is still pending, so a caller showing a
    // spinner does not blink idle between the keystroke and the request.
    isFetching: isFetching || typed !== term,
  };
};

/** Case-insensitive label match, for the two in-memory sections. */
export const filterByLabel = (options: RecipientOption[], query: string) => {
  const term = query.trim().toLowerCase();
  if (!term) return options;
  return options.filter((option) => option.label.toLowerCase().includes(term));
};
